import { Building2, Globe2, Loader2, Search } from "lucide-react";
import {
  JourneyField as Field,
  JourneySelect as Select,
  JourneyTextInput as TextInput,
} from "../../controls";
import {
  AWARD_OPTIONS,
  EVIDENCE_OPTIONS,
  JOURNEY_EDUCATION_OPTIONS,
  LEVEL_FROM_AI,
} from "../../options";

const NONSTANDARD_AWARDS = ["partial", "currently_studying", "not_awarded", "no_formal_diploma", "documents_unavailable"];

export const educationIsInsufficient = (answers) => {
  if (!answers.education_country || answers.education_country === "United Kingdom") return false;
  const weakLevel = !answers.highest_education || answers.highest_education === "unknown";
  const weakAward = NONSTANDARD_AWARDS.includes(answers.award_status);
  const weakEvidence = answers.evidence_status === "documents_unavailable";
  return (weakLevel || weakAward || weakEvidence) && !answers.education_description;
};

export const hasEnoughForAssessment = (answers) => (
  Boolean(answers.education_description || answers.education_institution || answers.qualification_name
    || (answers.highest_education && answers.highest_education !== "unknown"))
);

export const EducationStep = ({
  answers, update, updateMany, assessingEducation, onAssessEducation, onUseAssessedLevel,
  institutionCandidate, institutionLoading, onSearchInstitution, onConfirmInstitution, onRejectInstitution,
}) => {
  const isInternational = answers.education_country && answers.education_country !== "United Kingdom";
  const insufficient = educationIsInsufficient(answers);
  const needsInstitution = insufficient && !answers.education_institution;

  return (
    <div>
      <h2 className="font-display text-3xl font-bold tracking-tight text-ink">Tell us about your education in your own terms.</h2>
      <p className="mt-2 text-sm leading-6 text-slatey">You do not need to know the UK level. OpenDegree works out how your education may compare and only asks for what it still needs.</p>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <Field label="Country where you studied"><TextInput value={answers.education_country} onChange={(v) => update("education_country", v)} placeholder="For example: United Kingdom, Romania, India" testId="journey-education-country" /></Field>
        <Field label="What best describes your education?"><Select value={answers.highest_education} onChange={(v) => update("highest_education", v)} testId="journey-education-level" placeholder="Select or choose I don’t know" options={JOURNEY_EDUCATION_OPTIONS} /></Field>
        <Field label="Did you complete it?"><Select value={answers.award_status} onChange={(v) => update("award_status", v)} testId="journey-award-status" placeholder="Select status" options={AWARD_OPTIONS} /></Field>
        <Field label="Evidence available"><Select value={answers.evidence_status} onChange={(v) => update("evidence_status", v)} testId="journey-evidence-status" placeholder="Select evidence" options={EVIDENCE_OPTIONS} /></Field>
      </div>

      {needsInstitution && (
        <div className="mt-6 rounded-3xl border border-sky-200 bg-sky-50 p-5" data-testid="institution-panel">
          <h3 className="font-display text-xl font-bold text-ink">Let’s identify your education</h3>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slatey">We need a little more information before we can assess how your education may compare with UK requirements.</p>
          <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
            <Field label="What is the name of the school, college or university you attended?">
              <TextInput value={answers.institution_search || ""} onChange={(v) => update("institution_search", v)} placeholder="For example: Universitatea „Ștefan cel Mare” din Suceava" testId="journey-institution-input" />
            </Field>
            <button onClick={onSearchInstitution} disabled={institutionLoading || !(answers.institution_search || "").trim()} data-testid="journey-institution-search"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3.5 text-sm font-bold text-white disabled:opacity-50">
              {institutionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Find my institution
            </button>
          </div>

          {institutionCandidate?.name && (
            <div className="mt-4 rounded-2xl bg-white p-4" data-testid="institution-candidate">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-cobalt">We think we’ve identified your institution</p>
              <p className="mt-2 flex items-start gap-2 font-display text-lg font-bold text-ink"><Building2 className="mt-1 h-5 w-5 shrink-0 text-cobalt" /> {institutionCandidate.name}</p>
              <p className="mt-1 text-sm text-slatey">{[institutionCandidate.city, institutionCandidate.country].filter(Boolean).join(", ")}{institutionCandidate.institution_type && institutionCandidate.institution_type !== "unclear" ? ` · ${institutionCandidate.institution_type[0].toUpperCase()}${institutionCandidate.institution_type.slice(1)}` : ""}</p>
              <p className="mt-3 text-sm font-semibold text-ink">Is this your institution?</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button onClick={onConfirmInstitution} data-testid="journey-institution-confirm" className="rounded-full bg-cobalt px-4 py-2.5 text-sm font-bold text-white">Yes, that’s my institution</button>
                <button onClick={onRejectInstitution} data-testid="journey-institution-reject" className="rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-ink">No, search again</button>
              </div>
            </div>
          )}
        </div>
      )}

      {answers.education_institution && (
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="institution-confirmed">
          <p className="text-sm font-bold text-emerald-800">Institution confirmed: {answers.education_institution}{answers.education_institution_country ? ` · ${answers.education_institution_country}` : ""}</p>
          <button onClick={() => updateMany({ education_institution: "", education_institution_type: "", education_institution_country: "" })} data-testid="journey-institution-change" className="mt-2 text-xs font-bold text-cobalt underline">Change institution</button>
        </div>
      )}

      {(insufficient || answers.education_institution) && (
        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <Field label="What was the qualification called?" help="Use the original name in your own language if that is easier.">
            <TextInput value={answers.qualification_name || ""} onChange={(v) => update("qualification_name", v)} placeholder="For example: Diplomă de Licență" testId="journey-qualification-name" />
          </Field>
          <Field label="When did you study? (optional)">
            <TextInput value={answers.study_period || ""} onChange={(v) => update("study_period", v)} placeholder="For example: 2018 – 2022" testId="journey-study-period" />
          </Field>
        </div>
      )}

      <div className="mt-5">
        <Field label="Anything else about your studies? (optional)" help="Use terms you recognise — partial study, professional diplomas or overseas degrees all count.">
          <textarea value={answers.education_description || ""} onChange={(e) => update("education_description", e.target.value)} data-testid="journey-education-description" rows={3} className="w-full rounded-2xl border border-slate-200 p-4 text-sm leading-6 outline-none focus:border-cobalt" />
        </Field>
      </div>

      {isInternational && hasEnoughForAssessment(answers) && (
        <div className="mt-6 rounded-3xl border border-sky-200 bg-sky-50 p-5" data-testid="uk-equivalent-panel">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="flex items-center gap-2 font-display text-xl font-bold text-ink"><Globe2 className="h-5 w-5 text-cobalt" /> Assess UK equivalent</h3>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slatey">OpenDegree can interpret your declared education and evidence into a potential UK level, and feed it straight into this opportunity’s eligibility check. This is indicative and not official recognition.</p>
            </div>
            <button onClick={onAssessEducation} disabled={assessingEducation} data-testid="assess-uk-equivalent-button" className="inline-flex items-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
              {assessingEducation && <Loader2 className="h-4 w-4 animate-spin" />} Assess UK equivalent
            </button>
          </div>
          {answers.education_assessment && (
            <div className="mt-4 rounded-2xl bg-white p-4" data-testid="uk-equivalent-result">
              <p className="text-lg font-bold text-cobalt">Potential UK level: {answers.education_assessment.potential_uk_level}</p>
              <p className="mt-2 text-sm leading-6 text-slatey">{answers.education_assessment.summary}</p>
              <p className="mt-2 text-xs font-semibold text-amber-700">{answers.education_assessment.official_status}</p>
              {LEVEL_FROM_AI[answers.education_assessment.potential_uk_level] && <button onClick={onUseAssessedLevel} data-testid="use-uk-level-button" className="mt-3 rounded-full border border-cobalt px-4 py-2 text-sm font-bold text-cobalt">Use this as my current level</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
